import React, { useState, useEffect } from 'react';
import { getConsumableUsageHistory } from '../../utils/itemOperations';

const ConsumableUsageHistory = ({ itemId, reloadKey = 0 }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadUsageHistory();
  }, [itemId, reloadKey]);

  const loadUsageHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const usageHistory = await getConsumableUsageHistory(itemId, 50);
      setHistory(usageHistory);
    } catch (error) {
      console.error('Error loading usage history:', error);
      setError('Failed to load usage history');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatUser = (record) => {
    if (record.isRestock) {
      return 'Admin (Restock)';
    }
    return record.userName || (record.pid ? `Student ${record.pid}` : 'Unknown User');
  };

  const displayedHistory = showAll ? history : history.slice(0, 10);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-600">Loading usage history...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Usage History</h3>
        {history.length > 10 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showAll ? 'Show Less' : `Show All (${history.length})`}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
          {error}
        </div>
      )}

      {history.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No usage history found for this consumable.
        </div>
      ) : (
        <div className="space-y-3">
          {displayedHistory.map((record) => (
            <div
              key={record.id}
              className={`p-4 rounded-lg border-l-4 ${
                record.isRestock
                  ? 'bg-green-50 border-green-400'
                  : 'bg-gray-50 border-blue-400'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {formatUser(record)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      record.isRestock
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {record.isRestock ? 'Restocked' : 'Used'}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span>
                        <strong>Amount:</strong> 
                        {record.isRestock ? '+' : '-'}{Math.abs(record.amountUsed)} {record.unit}
                      </span>
                      <span>
                        <strong>Stock:</strong>
                        {record.stockBefore} → {record.stockAfter} {record.unit}
                      </span>
                    </div>
                    
                    {record.notes && (
                      <div className="mt-2">
                        <strong>Notes:</strong> {record.notes}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right mt-2 sm:mt-0">
                  <div className="text-sm text-gray-500">
                    {formatDateTime(record.timestamp)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-gray-900">
                {history.filter(r => !r.isRestock).length}
              </div>
              <div className="text-sm text-gray-600">Total Uses</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">
                {history.filter(r => r.isRestock).length}
              </div>
              <div className="text-sm text-gray-600">Restocks</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">
                {history
                  .filter(r => !r.isRestock)
                  .reduce((sum, r) => sum + Math.abs(r.amountUsed), 0)
                  .toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">Total Used</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsumableUsageHistory;

